/**
 * PlayPage - In-Game Status Board
 *
 * Mobile-first status board for live play:
 * - Shows wizard, apprentice, and soldiers with fixed stats from warbandSlice
 * - Tracks current health, tags, and notes per figure via playSlice
 * - Provides an informational phase indicator and quick rule reference chips
 */

import { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Label,
} from '@/components/ui';
import { cn } from '@/utils';
import {
  useWarbandStore,
  selectWizard,
  selectApprentice,
  selectSoldiers,
  selectKnownSpells,
  SOLDIER_TEMPLATES,
} from '@/store/warbandSlice';
import { usePlayStore, selectStatuses, type EntityRef, type Status } from '@/store/playSlice';
import {
  Heart,
  Shield,
  Brain,
  Footprints,
  Swords,
  Target,
  Bookmark,
  Info,
} from 'lucide-react';
import { getSpellsByIds, getEffectiveCastingNumber, type Spell } from '@/data/spellData';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMMON_TAGS = ['Carrying Treasure', 'Encumbered', 'Group Activated'] as const;

type RuleId = 'actions' | 'treasure' | 'spellcasting';

interface RuleConfig {
  id: RuleId;
  label: string;
  description: string;
  body: string;
}

const RULES: RuleConfig[] = [
  {
    id: 'actions',
    label: 'Actions & Phases',
    description: 'Two actions per activation; group activations must Move first.',
    body:
      'Each figure may take up to two actions on its activation (Move, Fight, Shoot, Cast, Pick up/Drop, or Special), ' +
      'as summarized on the public Frostgrave Quick Reference Sheet. When declaring a group activation, the first action ' +
      'taken by the group must be Move; the second action may be any legal action. This board only tracks phase and tags— ' +
      'you remain responsible for enforcing the action limits at the table.',
  },
  {
    id: 'treasure',
    label: 'Treasure Rules',
    description: 'Carrying treasure slows movement and can cause Encumbered tags.',
    body:
      'The public Quick Reference Sheet specifies that a figure cannot pick up treasure within 1", may carry at most one ' +
      'treasure token, and moves at half speed while carrying it. Figures carrying treasure with certain equipment ' +
      '(e.g., heavy weapons or shields) are often treated as Encumbered, typically suffering a -1 Fight modifier. ' +
      'Use the “Carrying Treasure” and “Encumbered” tags on this board to reflect your table state; rules are not enforced automatically.',
  },
  {
    id: 'spellcasting',
    label: 'Spellcasting',
    description: 'Apprentices cast at -2; Empower costs 1 Health per +1; failures can cause damage.',
    body:
      'Spellcasting in Frostgrave uses the spell’s Casting Number from the public Spell Cards or reference. Apprentices apply ' +
      'a -2 modifier to their casting rolls compared to wizards. Empowering a spell increases the roll at the cost of 1 Health ' +
      'per +1 granted. On a failed cast, consult the failure table from the public Quick Reference Sheet to determine damage based ' +
      'on how far the roll missed the Casting Number by. This board shows spell data and lets you track health but does not roll dice or apply damage.',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getEntityId(ref: EntityRef): string {
  if (ref.kind === 'wizard') return 'wizard';
  if (ref.kind === 'apprentice') return 'apprentice';
  if (ref.kind === 'soldier') return `soldier:${ref.id}`;
  return ref.id ?? 'custom';
}

function getCurrentHealth(status: Status | undefined, fallbackMax: number): number {
  const current = status?.health?.current;
  if (typeof current === 'number') {
    return Math.max(0, Math.min(current, status?.health?.max ?? fallbackMax));
  }
  return fallbackMax;
}

function toggleTagArray(existing: string[] | undefined, tag: string): string[] {
  const tags = existing ?? [];
  return tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

interface FigureCardProps {
  title: string;
  subtitle: string;
  entityRef: EntityRef;
  stats: {
    move: number;
    fight: number;
    shoot: number;
    armor: number;
    will: number;
    health: number;
    cost?: number;
  };
  displayName?: string | undefined;
}

function FigureCard({ title, subtitle, entityRef, stats, displayName }: FigureCardProps) {
  const playStore = usePlayStore();
  const statuses = usePlayStore(selectStatuses);

  const entityId = getEntityId(entityRef);
  const status = statuses[entityId];
  const name = status?.name ?? displayName ?? title;

  const currentHealth = getCurrentHealth(status, stats.health);
  const [noteDraft, setNoteDraft] = useState(status?.notes ?? '');

  const handleHealthChange = (delta: number) => {
    const max = stats.health;
    const next = Math.max(0, Math.min(currentHealth + delta, max));

    const baseStatus: Status = {
      ref: entityRef,
      name,
      health: { current: next, max },
      armor: stats.armor,
      will: stats.will,
      tags: status?.tags ?? [],
      notes: status?.notes,
    };

    if (!status) {
      playStore.upsertStatus(entityId, baseStatus);
    } else {
      playStore.patchStatus(entityId, {
        health: { current: next, max },
      });
    }
  };

  const handleTagToggle = (tag: string) => {
    const nextTags = toggleTagArray(status?.tags, tag);
    if (!status) {
      playStore.upsertStatus(entityId, {
        ref: entityRef,
        name,
        health: { current: stats.health, max: stats.health },
        armor: stats.armor,
        will: stats.will,
        tags: nextTags,
      });
    } else {
      playStore.patchStatus(entityId, { tags: nextTags });
    }
  };

  const handleNotesBlur = () => {
    const trimmed = noteDraft.trim();
    if (!status && !trimmed) return;

    if (!status) {
      playStore.upsertStatus(entityId, {
        ref: entityRef,
        name,
        health: { current: stats.health, max: stats.health },
        armor: stats.armor,
        will: stats.will,
        tags: [],
        notes: trimmed,
      });
    } else {
      playStore.patchStatus(entityId, { notes: trimmed });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription className="text-xs">{subtitle}</CardDescription>
          </div>
          {displayName && (
            <Badge variant="outline" className="text-[10px]">
              {displayName}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 text-xs sm:grid-cols-6">
          <StatPill icon={<Footprints className="h-3 w-3" />} label="Move" value={stats.move} />
          <StatPill icon={<Swords className="h-3 w-3" />} label="Fight" value={stats.fight} />
          <StatPill icon={<Target className="h-3 w-3" />} label="Shoot" value={stats.shoot} />
          <StatPill icon={<Shield className="h-3 w-3" />} label="Armor" value={stats.armor} />
          <StatPill icon={<Brain className="h-3 w-3" />} label="Will" value={stats.will} />
          <div className="flex flex-col items-center justify-center rounded-md bg-muted px-2 py-1">
            <span className="flex items-center gap-1 text-[11px] font-medium">
              <Heart className="h-3 w-3 text-destructive" />
              Health
            </span>
            <span className="font-mono text-sm">
              {currentHealth} / {stats.health}
            </span>
          </div>
        </div>

        {/* Health Controls */}
        <div className="flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="min-h-[44px] min-w-[44px] rounded-full"
            onClick={() => handleHealthChange(-1)}
            aria-label={`Decrease health for ${name}`}
          >
            -1
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="min-h-[44px] min-w-[44px] rounded-full"
            onClick={() => handleHealthChange(1)}
            aria-label={`Increase health for ${name}`}
          >
            +1
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="min-h-[44px] min-w-[44px] rounded-full text-xs"
            onClick={() => {
              const max = stats.health;
              if (!status) {
                playStore.upsertStatus(entityId, {
                  ref: entityRef,
                  name,
                  health: { current: max, max },
                  armor: stats.armor,
                  will: stats.will,
                  tags: [],
                });
              } else {
                playStore.patchStatus(entityId, {
                  health: { current: max, max },
                });
              }
            }}
            aria-label={`Reset health for ${name}`}
          >
            Reset
          </Button>
        </div>

        {/* Tags */}
        <div className="space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground">Tags</span>
          <div className="flex flex-wrap gap-2">
            {COMMON_TAGS.map((tag) => {
              const isActive = status?.tags?.includes(tag) ?? false;
              return (
                <Button
                  key={tag}
                  size="sm"
                  variant={isActive ? 'default' : 'outline'}
                  className={cn(
                    'min-h-[32px] rounded-full border-dashed px-3 py-1 text-[11px]',
                    isActive && 'border-solid'
                  )}
                  onClick={() => handleTagToggle(tag)}
                  aria-pressed={isActive}
                  aria-label={`${isActive ? 'Remove' : 'Add'} tag ${tag} for ${name}`}
                >
                  {tag}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-1">
          <Label htmlFor={`${entityId}-notes`} className="text-[11px]">
            Notes
          </Label>
          <textarea
            id={`${entityId}-notes`}
            className="min-h-[60px] w-full rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onBlur={handleNotesBlur}
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface StatPillProps {
  icon: React.ReactNode;
  label: string;
  value: number;
}

function StatPill({ icon, label, value }: StatPillProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md bg-muted px-2 py-1">
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="font-mono text-sm">{value}</span>
    </div>
  );
}

interface RuleSheetProps {
  rule: RuleConfig | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function RuleSheet({ rule, open, onOpenChange }: RuleSheetProps) {
  if (!rule) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-auto">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-primary" />
            <SheetTitle>{rule.label}</SheetTitle>
          </div>
          <SheetDescription>{rule.description}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3 text-sm leading-relaxed">
          <p>{rule.body}</p>
          <p className="text-xs text-muted-foreground">
            Source: Frostgrave 2e public Quick Reference Sheet and Spell Cards.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface SpellSummaryProps {
  spells: Spell[];
}

function SpellSummary({ spells }: SpellSummaryProps) {
  if (!spells.length) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Spells</CardTitle>
        </div>
        <CardDescription className="text-xs">
          Attached spells from your spellbook (Casting Numbers from public Spell Cards).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="grid gap-2 sm:grid-cols-2">
          {spells.map((spell) => (
            <div
              key={spell.id}
              className="flex flex-col gap-1 rounded-md border border-border bg-card px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{spell.name}</span>
                <Badge variant="outline" className="text-[10px]">
                  {spell.school}
                </Badge>
              </div>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span>
                  Wizard CN{' '}
                  <span className="font-mono font-semibold">
                    {getEffectiveCastingNumber(spell, spell.school, false)}
                  </span>
                </span>
                <span className="text-muted-foreground">
                  Apprentice CN{' '}
                  <span className="font-mono">
                    {getEffectiveCastingNumber(spell, spell.school, true)}
                  </span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function PlayPage() {
  const wizard = useWarbandStore(selectWizard);
  const apprentice = useWarbandStore(selectApprentice);
  const soldiers = useWarbandStore(selectSoldiers);
  const knownSpellIds = useWarbandStore(selectKnownSpells);

  const [activeRuleId, setActiveRuleId] = useState<RuleId | null>(null);

  const spells = useMemo(() => getSpellsByIds(knownSpellIds), [knownSpellIds]);

  const currentRule = activeRuleId ? RULES.find((r) => r.id === activeRuleId) ?? null : null;

  const wizardStats = wizard.stats;
  const apprenticeStats = apprentice?.stats;

  return (
    <div className="min-h-screen pb-safe">
      {/* Sticky Rules Bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container flex flex-col gap-2 px-4 py-2">
          {/* Rule Chips */}
          <div className="flex flex-wrap gap-2 pb-1">
            {RULES.map((rule) => (
              <Button
                key={rule.id}
                variant="ghost"
                size="sm"
                className="min-h-[32px] rounded-full border border-border px-3 py-1 text-[11px]"
                onClick={() => setActiveRuleId(rule.id)}
                aria-label={`Show rule: ${rule.label}`}
              >
                <Info className="mr-1 h-3 w-3" />
                {rule.label}
              </Button>
            ))}
          </div>
        </div>
      </header>

      <main className="container space-y-4 px-4 py-4 pb-24">
        {/* Wizard / Apprentice */}
        <section aria-labelledby="casters-heading" className="space-y-3">
          <h2 id="casters-heading" className="text-sm font-semibold text-muted-foreground">
            Casters
          </h2>
          <FigureCard
            title="Wizard"
            subtitle={wizard.name || 'Warband Leader'}
            entityRef={{ kind: 'wizard' }}
            stats={{
              move: wizardStats.move,
              fight: wizardStats.fight,
              shoot: wizardStats.shoot,
              armor: wizardStats.armor,
              will: wizardStats.will,
              health: wizard.health.max,
            }}
            displayName={wizard.name || undefined}
          />
          {apprentice && apprenticeStats && (
            <FigureCard
              title="Apprentice"
              subtitle={apprentice.name || 'Casts at -2 compared to Wizard'}
              entityRef={{ kind: 'apprentice' }}
              stats={{
                move: apprenticeStats.move,
                fight: apprenticeStats.fight,
                shoot: apprenticeStats.shoot,
                armor: apprenticeStats.armor,
                will: apprenticeStats.will,
                health: apprentice.health.max,
              }}
              displayName={apprentice.name || undefined}
            />
          )}
        </section>

        {/* Soldiers */}
        <section aria-labelledby="soldiers-heading" className="space-y-3">
          <h2 id="soldiers-heading" className="text-sm font-semibold text-muted-foreground">
            Soldiers
          </h2>
          {soldiers.length === 0 ? (
            <Card>
              <CardContent className="py-4 text-xs text-muted-foreground">
                Build your warband on the Build tab to see soldier cards here.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {soldiers.map((soldier) => {
                const template = SOLDIER_TEMPLATES[soldier.templateId];
                if (!template) return null;

                return (
                  <FigureCard
                    key={soldier.id}
                    title={soldier.name || template.name}
                    subtitle={template.name}
                    entityRef={{ kind: 'soldier', id: soldier.id }}
                    stats={{
                      move: template.move,
                      fight: template.fight,
                      shoot: template.shoot,
                      armor: template.armor,
                      will: template.will,
                      health: template.health,
                      cost: template.cost,
                    }}
                    displayName={soldier.name || undefined}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* Spells Summary */}
        <section aria-labelledby="spells-heading">
          <h2 id="spells-heading" className="sr-only">
            Spells
          </h2>
          <SpellSummary spells={spells} />
        </section>
      </main>

      <RuleSheet
        rule={currentRule}
        open={Boolean(currentRule)}
        onOpenChange={(open) => !open && setActiveRuleId(null)}
      />
    </div>
  );
}


